"use client";

import { useState } from "react";
import {
  Bar,
  BarChart,
  Area,
  AreaChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import useSWR from "swr";
import { useRollingMonthlyUsage } from "@/hooks/use-rolling-monthly-usage";
import { UsageUnavailable } from "@/components/usage-unavailable";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  getDailyStatisticsLast30Days,
  type MonthlyUsageStats,
} from "@/services/statistics.service";

const transformDailyStatsToChartData = (stats: MonthlyUsageStats[]) => {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return stats.map((stat) => {
    const parts = stat.period.split("-");
    let name = stat.period;
    if (parts.length === 3) {
      const monthIndex = parseInt(parts[1]) - 1;
      const day = parseInt(parts[2]);
      name = `${months[monthIndex]} ${day}`;
    }

    return {
      name,
      period: stat.period,
      "Merchant API": stat.merchantApi,
      "Youzu Lens": stat.youzuLens,
      "Property Visualize": stat.propertyVisualize,
      "Room Visualizer": stat.roomVisualizer,
      "Magic Background": stat.magicBackground,
      "2D to 3D": stat.twoDTo3D,
    };
  });
};

export function FeatureUsageChart() {
  const [viewMode, setViewMode] = useState<"monthly" | "daily">("daily");

  const { data: monthlyData, isLoading: monthlyLoading, isUnavailable: monthlyUnavailable } =
    useRollingMonthlyUsage(viewMode === "monthly");

  const { data: dailyResponse, error: dailyError, isLoading: dailyLoading } = useSWR(
    viewMode === "daily" ? "daily-stats-last-30-days" : null,
    getDailyStatisticsLast30Days,
    { refreshInterval: 60000 },
  );

  const isUnavailable = viewMode === "monthly"
    ? monthlyUnavailable
    : Boolean(dailyError) || dailyResponse?.success === false;
  const isLoading = viewMode === "monthly" ? monthlyLoading : dailyLoading || !dailyResponse;

  const renderContent = () => {
    if (isUnavailable) {
      return (
        <div className="h-[300px] w-full flex items-center justify-center">
          <UsageUnavailable className="text-center" />
        </div>
      );
    }
    if (isLoading) {
      return (
        <div className="h-[300px] w-full flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-purple-500 border-r-transparent" />
            <p className="mt-2 text-sm text-muted-foreground">
              Loading chart data...
            </p>
          </div>
        </div>
      );
    }

    let data = [];

    if (viewMode === "monthly") {
      data = monthlyData.map(({ name, period, stats }) => ({
        name,
        period,
        "Merchant API": stats?.merchantApi ?? 0,
        "Youzu Lens": stats?.youzuLens ?? 0,
        "Property Visualize": stats?.propertyVisualize ?? 0,
        "Room Visualizer": stats?.roomVisualizer ?? 0,
        "Magic Background": stats?.magicBackground ?? 0,
        "2D to 3D": stats?.twoDTo3D ?? 0,
      }));
    } else {
      if (!dailyResponse?.data.length) {
        return (
          <div className="h-[300px] w-full flex items-center justify-center">
            <p className="text-gray-600">No data available for this period</p>
          </div>
        );
      }
      data = transformDailyStatsToChartData(dailyResponse.data);
    }

    if (viewMode === "daily") {
      return (
        <div className="h-[300px] w-full mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="colorMerchant" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorYouzu" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FF4D8C" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#FF4D8C" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorProperty" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#9D4EDD" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#9D4EDD" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorRoom" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4CC9F0" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#4CC9F0" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorMagic" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="color2D" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F3A712" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#F3A712" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12, fill: '#666' }} 
                tickMargin={10}
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#666' }} 
                axisLine={false}
                tickLine={false}
              />
              <Tooltip 
                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
              />
              <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px' }} />
              <Area type="monotone" dataKey="Merchant API" stroke="#6366F1" strokeWidth={2} fillOpacity={1} fill="url(#colorMerchant)" />
              <Area type="monotone" dataKey="Youzu Lens" stroke="#FF4D8C" strokeWidth={2} fillOpacity={1} fill="url(#colorYouzu)" />
              <Area type="monotone" dataKey="Property Visualize" stroke="#9D4EDD" strokeWidth={2} fillOpacity={1} fill="url(#colorProperty)" />
              <Area type="monotone" dataKey="Room Visualizer" stroke="#4CC9F0" strokeWidth={2} fillOpacity={1} fill="url(#colorRoom)" />
              <Area type="monotone" dataKey="Magic Background" stroke="#10B981" strokeWidth={2} fillOpacity={1} fill="url(#colorMagic)" />
              <Area type="monotone" dataKey="2D to 3D" stroke="#F3A712" strokeWidth={2} fillOpacity={1} fill="url(#color2D)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      );
    }

    return (
      <div className="h-[300px] w-full mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 12, fill: '#666' }}
              tickMargin={10}
              axisLine={false}
              tickLine={false} 
            />
            <YAxis 
              tick={{ fontSize: 12, fill: '#666' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip 
              cursor={{ fill: '#f3f4f6' }}
              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
            />
            <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px' }} />
            <Bar dataKey="Merchant API" fill="#6366F1" radius={[4, 4, 0, 0]} maxBarSize={40} />
            <Bar dataKey="Youzu Lens" fill="#FF4D8C" radius={[4, 4, 0, 0]} maxBarSize={40} />
            <Bar dataKey="Property Visualize" fill="#9D4EDD" radius={[4, 4, 0, 0]} maxBarSize={40} />
            <Bar dataKey="Room Visualizer" fill="#4CC9F0" radius={[4, 4, 0, 0]} maxBarSize={40} />
            <Bar dataKey="Magic Background" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={40} />
            <Bar dataKey="2D to 3D" fill="#F3A712" radius={[4, 4, 0, 0]} maxBarSize={40} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <div className="w-full">
      <div className="flex justify-end mb-2">
        <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as "monthly" | "daily")}>
          <TabsList>
            <TabsTrigger value="daily">Daily</TabsTrigger>
            <TabsTrigger value="monthly">Monthly</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      {renderContent()}
    </div>
  );
}
